import { listMaterialStores, type Stn } from '@api/warehouse-ops';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

type ReceivableStn = Pick<Stn, 'id' | 'code' | 'itemType' | 'toNodeId' | 'toNodeKind' | 'toSiteId'>;

/**
 * Where a transfer is received, so the stock lands where it was sent:
 *  - material to a vendor site: that site's Receive tab;
 *  - material to one of our warehouses: its RM/PM receipt, with the transfer picked - the same
 *    receipt, gate QC and all, whether or not the warehouse is a marked material store;
 *  - finished goods to one of our warehouses: the finished-goods GRN form, filled from it.
 * Null when there is no such page: a channel, customer or vendor party (they receive on their
 * side), or one of our warehouses while Warehouse Ops - where those receipts live - is off.
 *
 * `receivingWarehouseIds` is the brand's own warehouses with Warehouse Ops on: the list the
 * RM/PM receipt offers (`listMaterialStores`), empty when Warehouse Ops is off.
 */
export function stnReceivePath(stn: ReceivableStn, receivingWarehouseIds: ReadonlySet<string>): string | null {
    const isMaterial = stn.itemType === 'material';
    if (stn.toSiteId && isMaterial) {
        return `/cm-sites/${encodeURIComponent(stn.toSiteId)}?tab=receive&stn=${encodeURIComponent(stn.code)}`;
    }
    if (stn.toNodeKind !== 'INTERNAL' || !receivingWarehouseIds.has(stn.toNodeId)) return null;
    if (isMaterial) {
        return `/warehouse-ops/inbound?store=${encodeURIComponent(stn.toNodeId)}&stn=${encodeURIComponent(stn.code)}`;
    }
    return `/warehouse-ops/inbound?createGrn=STN&stnId=${encodeURIComponent(stn.id)}`;
}

/**
 * `stnReceivePath` with the brand's receiving warehouses read once (the Inbound page's cached
 * list). `ready` is false until they are known, so no Receive shows before it can be routed.
 */
export function useStnReceivePath(): { pathFor: (stn: ReceivableStn) => string | null; ready: boolean } {
    const { data: stores, isSuccess } = useQuery(['material-stores'], listMaterialStores, { staleTime: 5 * 60 * 1000 });
    const pathFor = useCallback(
        (stn: ReceivableStn) => stnReceivePath(stn, new Set((stores ?? []).map((store) => store.warehouseId))),
        [stores],
    );
    return { pathFor, ready: isSuccess };
}
